import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X, AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from './AccountAvatar';

type PendingAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
  connectedAt: string;
  lastSyncedAt: string;
};

type NetworkLimit = {
  platform: Platform;
  maxAccounts: number;     // -1 unlimited
  currentCount: number;
  available: number;
};

type Props = {
  onClose: () => void;
};

export function PendingAccountsModal({ onClose }: Props) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const pendingQuery = useQuery({
    queryKey: ['social', 'pending'],
    queryFn: () => api<{ accounts: PendingAccount[] }>('/api/social/accounts/pending'),
  });

  const limitsQuery = useQuery({
    queryKey: ['social', 'limits'],
    queryFn: () => api<{ limits: NetworkLimit[] }>('/api/social/limits'),
  });

  const accounts = pendingQuery.data?.accounts ?? [];
  const limits = limitsQuery.data?.limits ?? [];

  // Default-select nothing — when a Facebook business connection returns
  // dozens of Instagram accounts but the plan only allows 1, pre-checking
  // everything forces the user to uncheck 60+ items by hand. Better make
  // them pick deliberately, respecting their plan limit.

  const limitByPlatform = useMemo(() => {
    const m = new Map<Platform, NetworkLimit>();
    for (const l of limits) m.set(l.platform, l);
    return m;
  }, [limits]);

  const grouped = useMemo(() => {
    const m = new Map<Platform, PendingAccount[]>();
    for (const a of accounts) {
      const arr = m.get(a.platform) ?? [];
      arr.push(a);
      m.set(a.platform, arr);
    }
    return m;
  }, [accounts]);

  // Selected count per platform
  const selectedCountByPlatform = useMemo(() => {
    const m = new Map<Platform, number>();
    for (const a of accounts) {
      if (selected.has(a.id)) {
        m.set(a.platform, (m.get(a.platform) ?? 0) + 1);
      }
    }
    return m;
  }, [accounts, selected]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const resolveMutation = useMutation({
    mutationFn: async () => {
      const keepIds = Array.from(selected);
      const rejectIds = accounts.filter((a) => !selected.has(a.id)).map((a) => a.id);
      return api('/api/social/accounts/resolve', {
        method: 'POST',
        body: JSON.stringify({ keepIds, rejectIds }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['social', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['social', 'limits'] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setErrorMsg(err.message);
      else setErrorMsg('No se pudo confirmar la selección.');
    },
  });

  const isLoading = pendingQuery.isLoading || limitsQuery.isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Revisar cuentas conectadas</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Has autorizado varias cuentas. Selecciona cuáles quieres mantener.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 size={16} className="animate-spin" /> Cargando…
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-slate-500 py-6">No hay cuentas pendientes de revisar.</div>
          ) : (
            <div className="space-y-6">
              {Array.from(grouped.entries()).map(([platform, group]) => {
                const meta = PLATFORM_META[platform];
                const Icon = meta.icon;
                const limit = limitByPlatform.get(platform);
                const selectedCount = selectedCountByPlatform.get(platform) ?? 0;
                const available = limit?.available ?? 0;
                const isUnlimited = limit?.maxAccounts === -1;
                const limitReached = !isUnlimited && limit !== undefined && selectedCount >= available;

                const subtitle = (() => {
                  if (!limit) return 'Plataforma no incluida en tu plan';
                  if (isUnlimited) return 'Cuentas ilimitadas';
                  return `${limit.currentCount} / ${limit.maxAccounts} conectadas — puedes añadir ${available} más`;
                })();

                return (
                  <div key={platform}>
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white shrink-0"
                        style={{ backgroundColor: meta.color }}
                      >
                        <Icon size={16} color="white" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{meta.label}</div>
                        <div className="text-xs text-slate-500">{subtitle}</div>
                      </div>
                    </div>

                    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                      {group.map((a) => {
                        const isSelected = selected.has(a.id);
                        const disabled = !isSelected && limitReached;
                        return (
                          <li
                            key={a.id}
                            className={`flex items-center gap-3 px-4 py-3 ${disabled ? 'opacity-50' : ''}`}
                          >
                            <label
                              className={`flex items-center gap-3 flex-1 min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              title={disabled ? 'Has alcanzado el límite del plan' : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={disabled}
                                onChange={() => toggle(a.id)}
                                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                              />
                              <AccountAvatar account={a} size={36} />
                              <div className="min-w-0">
                                <div className="font-medium text-slate-900 truncate">
                                  {a.displayName ?? a.username ?? '—'}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                  {a.username ? `@${a.username}` : meta.label}
                                </div>
                              </div>
                            </label>
                            {isSelected && (
                              <span className="text-emerald-600 shrink-0">
                                <Check size={16} />
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {errorMsg && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMsg}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-white">
          <div className="text-sm text-slate-500">
            {selected.size} de {accounts.length} seleccionada{accounts.length === 1 ? '' : 's'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={resolveMutation.isPending}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending || isLoading || accounts.length === 0}
              className="btn-accent gap-2"
            >
              {resolveMutation.isPending && <Loader2 size={14} className="animate-spin" />}
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
